import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useContactsApi } from 'src/modules/contacts/api';
import { getCurrentCompanyId } from 'src/modules/companies/slices';

export const useContactValidation = (
  refetchCompany: () =>
    | Promise<{ contactsRequiredValidation?: number } | undefined | void>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | any,
  onValidated: () => void,
) => {
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const { validateContacts } = useContactsApi();
  const [validationInProgress, setValidationInProgress] = useState(false);
  // Holds the live interval id. A ref (not state) so the polling callback below
  // reads the current id instead of the stale null captured at setInterval time.
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const handleValidateContacts = async () => {
    if (!selectedCompanyId) return;
    setValidationInProgress(true);

    try {
      await validateContacts(selectedCompanyId);

      pollingRef.current = setInterval(async () => {
        try {
          const updatedCompany = await refetchCompany();
          if (updatedCompany && updatedCompany.contactsRequiredValidation === 0) {
            setValidationInProgress(false);
            stopPolling();
            onValidated();
          }
        } catch (error) {
          console.error('Error polling company data:', error);
          setValidationInProgress(false);
          stopPolling();
        }
      }, 1000);
    } catch (error) {
      console.error('Error validating contacts:', error);
      setValidationInProgress(false);
    }
  };

  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  return { validationInProgress, handleValidateContacts };
};
